import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import HiveIcon from '@mui/icons-material/HiveOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Grow,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/AddOutlined';
import { cloneDeep, get, isEmpty, map } from 'lodash';
import React from 'react';
import { defaultClusteringParams, maxMaxK, minMinK } from './consts';
import { ClusteringParams } from './types';

export const ClusteringParamsList = ({
  clusteringParamsList,
  setClusteringParamsList,
  errors,
}: {
  clusteringParamsList: ClusteringParams[];
  setClusteringParamsList: (params: ClusteringParams[]) => void;
  errors: any;
}) => {
  const handleAddClusteringParams = () => {
    setClusteringParamsList([...clusteringParamsList, defaultClusteringParams]);
  };
  const handleRemoveClusteringParams = (index: number) => {
    const newClusteringParamsList = cloneDeep(clusteringParamsList);
    newClusteringParamsList.splice(index, 1);
    setClusteringParamsList(newClusteringParamsList);
  };

  return (
    <Grid container spacing={2}>
      {map(clusteringParamsList, (clusteringParams, index) => (
        <Grid item xs={12} key={index}>
          <ClusteringParamsAccordion
            deletable={clusteringParamsList.length > 1}
            index={index}
            clusteringParams={clusteringParams}
            setClusteringParams={(params) => {
              const newClusteringParamsList = cloneDeep(clusteringParamsList);
              newClusteringParamsList[index] = params;
              setClusteringParamsList(newClusteringParamsList);
            }}
            handleRemoveClusteringParams={() => handleRemoveClusteringParams(index)}
            errors={get(errors, `configParams.clusteringParams[${index}]`)}
          />
        </Grid>
      ))}
      <Grid item xs={12} justifyContent="flex-end" display="flex">
        <Grid container direction="column" alignItems="flex-end">
          <Grid item>
            <Button variant="outlined" onClick={handleAddClusteringParams} endIcon={<AddIcon />}>
              Add Parameters Set
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="caption" color="text.secondary" textAlign="right">
              Add one or more clustering parameter sets. Each set runs independently and produces its own neighborhood
              assignment, stored as a separate neighborhood ID column
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ClusteringParamsAccordion = ({
  deletable = true,
  index,
  clusteringParams,
  setClusteringParams,
  handleRemoveClusteringParams,
  errors,
}: {
  deletable?: boolean;
  index: number;
  clusteringParams: ClusteringParams;
  setClusteringParams: (params: ClusteringParams) => void;
  handleRemoveClusteringParams: () => void;
  errors: any;
}) => {
  const handleKMeansParamsChange = (field: string, value: number) => {
    setClusteringParams({
      ...clusteringParams,
      kMeansParams: {
        ...clusteringParams.kMeansParams,
        [field]: value,
      },
    });
  };

  const handleNeighborCountParamsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClusteringParams({
      ...clusteringParams,
      neighborCountParams: {
        ...clusteringParams.neighborCountParams,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : Number(event.target.value),
      },
    });
  };

  return (
    <Grid container spacing={1} justifyContent="flex-start" width={'100%'} alignItems="center">
      <Grid item pt={15} alignSelf={'self-start'}>
        <Grow in={deletable} timeout={500} unmountOnExit={true}>
          <IconButton
            aria-label="delete"
            size="large"
            onClick={handleRemoveClusteringParams}
            sx={{ cursor: 'pointer' }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Grow>
      </Grid>
      <Grid item xs>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandIcon />}
            sx={(theme) => ({ color: !isEmpty(errors) ? theme.palette.error.main : theme.palette.text.primary })}
          >
            <HiveIcon style={{ marginRight: 8 }} /> Neighborhoods Clustering {deletable ? `[${index + 1}]` : ''}
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ height: '100%', p: 2 }}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item alignItems="center" display="flex">
                      <DashboardIcon style={{ marginRight: 4 }} />
                      <Typography variant="body1">KMeans</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        K-means is evaluated for all K values between k min and k max. Each candidate is scored using
                        Davies–Bouldin and within-cluster sum of squares (WSS), and the best-supported K is selected,
                        subject to biological enrichment constraints
                      </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        label="Min K"
                        size="small"
                        value={clusteringParams.kMeansParams.minK}
                        type="number"
                        onChange={(event) => handleKMeansParamsChange('minK', Number(event.target.value))}
                        name="minK"
                        onBlur={() => {
                          if (clusteringParams.kMeansParams.minK < minMinK) {
                            handleKMeansParamsChange('minK', minMinK);
                          }
                          if (clusteringParams.kMeansParams.minK > maxMaxK) {
                            handleKMeansParamsChange('minK', maxMaxK);
                          }
                        }}
                        inputProps={{
                          step: 1,
                          min: minMinK,
                          max: maxMaxK,
                          type: 'number',
                          'aria-labelledby': 'input-slider',
                        }}
                        error={Boolean(get(errors, 'kMeansParams.minK'))}
                        helperText={get(errors, 'kMeansParams.minK')?.message}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        label="Max K"
                        size="small"
                        type="number"
                        name="maxK"
                        value={clusteringParams.kMeansParams.maxK}
                        onChange={(event) => handleKMeansParamsChange('maxK', Number(event.target.value))}
                        onBlur={() => {
                          if (clusteringParams.kMeansParams.maxK < minMinK) {
                            handleKMeansParamsChange('maxK', minMinK);
                          }
                          if (clusteringParams.kMeansParams.maxK > maxMaxK) {
                            handleKMeansParamsChange('maxK', maxMaxK);
                          }
                        }}
                        inputProps={{
                          step: 1,
                          min: minMinK,
                          max: maxMaxK,
                          type: 'number',
                          'aria-labelledby': 'input-slider',
                        }}
                        error={Boolean(get(errors, 'kMeansParams.maxK'))}
                        helperText={get(errors, 'kMeansParams.maxK')?.message}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ height: '100%', p: 2 }}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item display="flex" alignItems="center">
                      <HubIcon style={{ marginRight: 4 }} />
                      <Typography variant="body1">Neighbors Count Settings</Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        label="Number of Neighboring Cells (N Neighbors)"
                        size="small"
                        type="number"
                        name="nNeighbors"
                        value={clusteringParams.neighborCountParams.nNeighbors}
                        onChange={handleNeighborCountParamsChange}
                        error={Boolean(get(errors, 'neighborCountParams.nNeighbors'))}
                        helperText={get(errors, 'neighborCountParams.nNeighbors')?.message}
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clusteringParams.neighborCountParams.addSelfCellToNeighborCounts}
                            onChange={handleNeighborCountParamsChange}
                            name="addSelfCellToNeighborCounts"
                          />
                        }
                        label={`Add Self Cell to Neighbor Counts (${clusteringParams.neighborCountParams.nNeighbors} + 1)`}
                      />
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clusteringParams.neighborCountParams.normalizeCounts}
                            onChange={handleNeighborCountParamsChange}
                            name="normalizeCounts"
                          />
                        }
                        label="Normalize Counts to Fractions (0–1)"
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        label="arcsinh Normalization Coefficient"
                        type="number"
                        size="small"
                        disabled={!clusteringParams.neighborCountParams.normalizeCounts}
                        name="arcsinhNormalizationCoFactor"
                        value={clusteringParams.neighborCountParams.arcsinhNormalizationCoFactor}
                        onChange={handleNeighborCountParamsChange}
                        error={Boolean(get(errors, 'neighborCountParams.arcsinhNormalizationCoFactor'))}
                        helperText={
                          get(errors, 'neighborCountParams.arcsinhNormalizationCoFactor')?.message ||
                          `arcsinh(${clusteringParams.neighborCountParams.arcsinhNormalizationCoFactor} × (count per class/sum of the neighbors counts))`
                        }
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};
